import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/category/platform", label: t("nav.categories") },
    { to: "/category/promotions", label: t("nav.articles") },
    { to: "/about", label: t("nav.about") },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-vip-crown-fill text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
              必赢国际
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  isActive(link.to)
                    ? "text-primary-600 bg-primary-100/70"
                    : "text-foreground-700 hover:text-foreground-900 hover:bg-background-200/70"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/about"
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
            >
              登录
            </Link>
            <Link
              to="/about"
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("nav.register")}
            </Link>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-2">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? "text-primary-600 bg-primary-100/70"
                  : "text-foreground-700 hover:bg-background-200/70"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-2 flex gap-3">
            <Link
              to="/about"
              className="flex-1 text-center px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 border border-background-300 hover:bg-background-100 transition-colors"
            >
              登录
            </Link>
            <Link
              to="/about"
              className="flex-1 text-center px-4 py-3 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              {t("nav.register")}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}